import { isNullOrUndefined } from '@afterpay/utils'
import { useEffect, useState } from 'react'
import { useCheckBiometricStatusQuery, useCreateApplicantsMutation, useCreateInquiryIdMutation } from './apis'
import { Channel } from '@afterpaytouch/portal-api'
import {
  BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL,
  BiometricHardIdCheckHookStatus,
  PollingStatus,
  UseBiometricHardIdCheck,
  UseBiometricHardIdResponse,
  UseBiometricInquiryIdResponse,
} from '../../model'
import { IdentityCheckStatus } from '@afterpay/types'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useBiometricOptOut } from '../'
import { useGetIdentitiesQuery } from '@afterpaytouch/portal-api/consumer/account'

export const useBiometricHardId = (): UseBiometricHardIdResponse => {
  const [
    createApplicants,
    { data: applicantsData, isSuccess: isCreateApplicantsSuccess, isError: isCreateApplicantError, isLoading: isCreateApplicantLoading },
  ] = useCreateApplicantsMutation()

  useEffect(() => {
    if (isCreateApplicantsSuccess || !isNullOrUndefined(applicantsData)) {
      return
    }

    createApplicants({ referrer: new URL(window.location.origin), channel: Channel.CHECKOUT })
  }, [createApplicants, applicantsData, isCreateApplicantsSuccess])

  return {
    sdkToken: applicantsData?.sdkToken,
    applicantId: applicantsData?.applicantId,
    isCreateApplicantError,
    isCreateApplicantLoading,
  }
}

export const useBiometricInquiryId = (): UseBiometricInquiryIdResponse => {
  const biometricOptOut = useBiometricOptOut()
  const [createInquiryId, { data: inquiryIdData, isSuccess: isCreateInquiryIdSuccess, isError: isCreateInquiryIdError, isLoading: isCreateInquiryIdLoading }] =
    useCreateInquiryIdMutation()

  useEffect(() => {
    if (isCreateInquiryIdSuccess || !isNullOrUndefined(inquiryIdData)) {
      return
    }

    createInquiryId({ channel: Channel.CHECKOUT, biometricOptOut, biometricOptOutEnabled: typeof biometricOptOut === 'boolean' })
  }, [inquiryIdData, isCreateInquiryIdSuccess, biometricOptOut])

  return {
    sessionToken: inquiryIdData?.sessionToken,
    inquiryId: inquiryIdData?.inquiryId,
    isCreateInquiryIdError,
    isCreateInquiryIdLoading,
    isCreateInquiryIdSuccess,
  }
}

export const useBiometricHardIdCheck = (applicantId: string = '', skipBiometricCheck: boolean = false): UseBiometricHardIdCheck => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [status, setStatus] = useState(BiometricHardIdCheckHookStatus.INITIAL)
  const [pollingStatus, setPollingStatus] = useState<PollingStatus>(skipBiometricCheck ? PollingStatus.IDENTITIES : PollingStatus.CHECK)

  const biometricStatusPollingOption =
    pollingStatus === PollingStatus.CHECK
      ? {
          pollingInterval: BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL,
        }
      : { skip: true }

  const {
    isSuccess: isCheckSuccess,
    isError: isCheckError,
    isFetching,
  } = useCheckBiometricStatusQuery({ applicant_id: applicantId, channel: Channel.CHECKOUT }, biometricStatusPollingOption)

  const identitiesStatusPollingOption =
    pollingStatus === PollingStatus.IDENTITIES
      ? {
          pollingInterval: BIOMETRIC_VERIFYING_ACCOUNT_IDENTITY_POLLING_INTERVAL,
        }
      : {}

  const { data: identityBiometricCheckData } = useGetIdentitiesQuery(undefined, identitiesStatusPollingOption)

  useEffect(() => {
    if (!isFetching && (isCheckSuccess || isCheckError)) {
      setPollingStatus(PollingStatus.IDENTITIES)
    }
  }, [isFetching, isCheckSuccess, isCheckError])

  useEffect(() => {
    if (isFetching) {
      return
    }
    if (
      identityBiometricCheckData?.identityCheckStatus === IdentityCheckStatus.VERIFIED ||
      identityBiometricCheckData?.identityCheckStatus === IdentityCheckStatus.FAILED
    ) {
      logEvent(TrackingEvent.IDENTITY_CHECK_STATUS_UPDATED, { status: identityBiometricCheckData?.identityCheckStatus })
      setStatus(
        identityBiometricCheckData?.identityCheckStatus === IdentityCheckStatus.VERIFIED
          ? BiometricHardIdCheckHookStatus.VERIFIED
          : BiometricHardIdCheckHookStatus.FAILED
      )
      setPollingStatus(PollingStatus.STOP)
    }
  }, [identityBiometricCheckData])

  return {
    status,
  }
}
